import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { Helmet } from "react-helmet-async";
import { 
  CheckCircle, 
  FileText, 
  AlertTriangle,
  ArrowRight,
  Image,
  Star,
  MessageSquare,
  Phone,
  Layout,
  Link as LinkIcon
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const ServicePagesThatRank = () => {
  const faqs = [
    {
      question: "How many service pages should I have?",
      answer: "Create one page for each distinct service you want to be found for. Most lawn care companies need 4–8 pages (mowing, fertilization, aeration, leaf cleanup, etc.). Landscapers may need 6–12+ covering design, planting, hardscaping, irrigation, and maintenance."
    },
    {
      question: "Should I combine services on one page or split them?",
      answer: "Split them if people search for them separately. 'Lawn mowing' and 'lawn fertilization' have different search intent—separate pages rank better for each. Combine only when services are always sold together or have very low search volume individually."
    },
    {
      question: "How long should a service page be?",
      answer: "Aim for 600–1,000 words of helpful content. Include what the service is, who it's for, what's included, your process, pricing guidance, FAQs, and a clear call-to-action. Avoid fluff—every sentence should serve a purpose."
    },
    {
      question: "Do service pages need photos?",
      answer: "Yes. Photos of your actual work build trust and keep visitors on the page longer. Before-and-after shots are especially effective. Stock photos are better than nothing, but real project photos convert better."
    },
    {
      question: "Should I include pricing on service pages?",
      answer: "Include at least a starting price or price range. 'Starting at $45' or '$150–$300 depending on yard size' helps qualify leads. Visitors who see pricing and still contact you are more serious about hiring."
    },
    {
      question: "How do I optimize service pages for local SEO?",
      answer: "Include your city/region naturally in the title, H1, and body text. Link to your service area pages. Add schema markup. Make sure your NAP (name, address, phone) is consistent with your Google Business Profile."
    },
    {
      question: "How often should I update service pages?",
      answer: "Review them quarterly. Update pricing, add new photos, refresh testimonials, and adjust content based on what questions customers ask. Google favors fresh, maintained content."
    },
    {
      question: "Can I use the same service page structure for every service?",
      answer: "Yes—consistency helps visitors navigate your site. Use the same section order (hero, trust, details, process, photos, reviews, FAQ, CTA) but customize the content for each service."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.leafrushmarketing.com/service-pages-that-rank/#faq",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "description": "Done-for-you websites for lawn care and landscaping companies that drive calls, quotes, and recurring clients."
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.leafrushmarketing.com/service-pages-that-rank/#webpage",
    "name": "Service Pages That Rank: Structure Guide | Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com/service-pages-that-rank/",
    "description": "Learn how to structure service pages that rank in Google and convert visitors. Page-by-page breakdown for lawn care and landscaping websites.",
    "isPartOf": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    }
  };

  return (
    <>
      <SEO 
        title="Service Pages That Rank: Structure Guide"
        description="Learn how to structure service pages that rank in Google and convert visitors. Page-by-page breakdown for lawn care and landscaping websites."
        keywords="service pages, service pages that rank, lawn care service pages, landscaping service pages, mowing service page, hardscaping service page, service page SEO, local service page structure"
        canonical="https://www.leafrushmarketing.com/service-pages-that-rank/"
        ogUrl="https://www.leafrushmarketing.com/service-pages-that-rank/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>

      <div className="flex flex-col">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="container px-4 md:px-6 relative z-10">
            <div className="mx-auto max-w-4xl text-center space-y-6">
              <Badge variant="secondary" className="mb-4">
                Website Structure Guide
              </Badge>
              
              <h1 className="text-3xl md:text-5xl font-bold tracking-tight">
                Service Pages That Rank and Convert
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                A professional <NavLink to="/lawn-care-website-design/" className="text-primary hover:underline">lawn care website design</NavLink> or <NavLink to="/landscaping-website-design/" className="text-primary hover:underline">landscaping website design</NavLink> isn't complete without dedicated service pages that rank.
              </p>
              
              <p className="text-muted-foreground">
                Every lawn care web design we build includes 4–12 optimized service pages—each structured to rank for the exact service someone is searching for and convert them into a lead.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <a href="#get-started">
                  <Button size="lg" className="w-full sm:w-auto">
                    Get Started
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
                <NavLink to="/websites">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto">
                    See Website Pricing
                  </Button>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* Why Service Pages Matter */}
        <section className="py-12 md:py-16 bg-accent/30">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">Why Service Pages Drive More Calls</h2>
              
              <p className="text-muted-foreground mb-4">
                When someone searches "lawn aeration near me" or "patio installation Springfield," they are not browsing—they are ready to hire.
              </p>
              
              <p className="text-muted-foreground mb-4">
                Service pages let you rank for these high-intent searches. Instead of competing for generic terms like "landscaper," you show up for the exact service they need.
              </p>
              
              <div className="grid sm:grid-cols-3 gap-4 mt-8">
                <div className="p-4 bg-card rounded-lg border text-center">
                  <div className="text-3xl font-bold text-primary mb-1">47%</div>
                  <p className="text-sm text-muted-foreground">of searches are for specific services</p>
                </div>
                <div className="p-4 bg-card rounded-lg border text-center">
                  <div className="text-3xl font-bold text-primary mb-1">3x</div>
                  <p className="text-sm text-muted-foreground">higher conversion than homepage traffic</p>
                </div>
                <div className="p-4 bg-card rounded-lg border text-center">
                  <div className="text-3xl font-bold text-primary mb-1">82%</div>
                  <p className="text-sm text-muted-foreground">of visitors want service-specific info</p>
                </div>
              </div>
              
              <div className="mt-8 p-4 bg-card rounded-lg border">
                <p className="text-sm text-muted-foreground">
                  <strong>Related:</strong> We build optimized service pages into every website. See our{" "}
                  <NavLink to="/lawn-care-website-design/" className="text-primary hover:underline">lawn care website design</NavLink> and{" "}
                  <NavLink to="/landscaping-website-design/" className="text-primary hover:underline">landscaping website design</NavLink> packages.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* One Page vs Separate */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">One Page vs. Separate Service Pages</h2>
              
              <p className="text-muted-foreground mb-6">
                Should you list all services on one page or create separate pages? Here is the decision framework:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <Card className="border-green-500/30 bg-green-500/5">
                  <CardContent className="pt-6">
                    <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      Create Separate Pages When...
                    </h3>
                    <ul className="space-y-3 text-sm">
                      <li>• The service has its own search volume (check Google)</li>
                      <li>• People search for it independently ("mulch delivery")</li>
                      <li>• You want to rank for that specific keyword</li>
                      <li>• The service needs detailed explanation</li>
                      <li>• You have photos specific to that service</li>
                    </ul>
                  </CardContent>
                </Card>
                
                <Card className="border-orange-500/30 bg-orange-500/5">
                  <CardContent className="pt-6">
                    <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-orange-600" />
                      Keep On One Page When...
                    </h3>
                    <ul className="space-y-3 text-sm">
                      <li>• Services are always bundled together</li>
                      <li>• Very low individual search volume</li>
                      <li>• You are just starting and need content fast</li>
                      <li>• Services are variations (weekly vs. bi-weekly mowing)</li>
                      <li>• You cannot write 500+ unique words per service</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
              
              <p className="text-muted-foreground">
                <strong>Rule of thumb:</strong> If customers search for it separately, create a separate page.
              </p>
            </div>
          </div>
        </section>

        {/* The Exact Service Page Structure */}
        <section className="py-12 md:py-16 bg-accent/30">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">The Exact Service Page Structure</h2>
              
              <p className="text-muted-foreground mb-8">
                Use this section-by-section structure for every service page. The order matters—it mirrors how visitors make decisions.
              </p>
              
              <div className="space-y-6">
                <div className="p-5 bg-card rounded-lg border">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Layout className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">1. Hero Section</h3>
                      <p className="text-sm text-muted-foreground mt-1 mb-3">
                        First thing visitors see. Make it count.
                      </p>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li>• H1 with service + location: "Professional Lawn Aeration in Springfield"</li>
                        <li>• 1–2 sentence value proposition</li>
                        <li>• Primary CTA: "Get a Free Quote" or "Call Now"</li>
                        <li>• Optional: Phone number visible on desktop</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="p-5 bg-card rounded-lg border">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Star className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">2. Quick Trust Signals</h3>
                      <p className="text-sm text-muted-foreground mt-1 mb-3">
                        Build credibility before they scroll further.
                      </p>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li>• Years in business</li>
                        <li>• Number of customers served</li>
                        <li>• Google rating + review count</li>
                        <li>• Licenses, insurance, certifications</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="p-5 bg-card rounded-lg border">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <FileText className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">3. What Is Included / What You Get</h3>
                      <p className="text-sm text-muted-foreground mt-1 mb-3">
                        Spell out exactly what the service covers.
                      </p>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li>• Bullet list of deliverables</li>
                        <li>• What is NOT included (manages expectations)</li>
                        <li>• Pricing range or "starting at" price</li>
                        <li>• Frequency options if applicable</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="p-5 bg-card rounded-lg border">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <ArrowRight className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">4. Process / How It Works</h3>
                      <p className="text-sm text-muted-foreground mt-1 mb-3">
                        Remove uncertainty. Show them the path.
                      </p>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li>• Step 1: Request a quote (or call)</li>
                        <li>• Step 2: We visit/assess your property</li>
                        <li>• Step 3: You approve the estimate</li>
                        <li>• Step 4: We complete the work</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="p-5 bg-card rounded-lg border">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Image className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">5. Photos / Gallery</h3>
                      <p className="text-sm text-muted-foreground mt-1 mb-3">
                        Show your actual work. Real beats stock.
                      </p>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li>• Before/after comparisons</li>
                        <li>• Variety of project sizes</li>
                        <li>• Alt text with service + city for SEO</li>
                        <li>• Optimized file sizes for speed</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="p-5 bg-card rounded-lg border">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">6. Reviews / Testimonials</h3>
                      <p className="text-sm text-muted-foreground mt-1 mb-3">
                        Social proof specific to this service.
                      </p>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li>• 2–4 reviews mentioning the service</li>
                        <li>• Include reviewer name and city</li>
                        <li>• Link to Google reviews for more</li>
                        <li>• Video testimonials if available</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="p-5 bg-card rounded-lg border">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">7. FAQ Section</h3>
                      <p className="text-sm text-muted-foreground mt-1 mb-3">
                        Answer objections before they become barriers.
                      </p>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li>• 4–6 questions specific to this service</li>
                        <li>• How long does it take?</li>
                        <li>• What does it cost?</li>
                        <li>• How often should I schedule this?</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="p-5 bg-card rounded-lg border">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">8. CTA + Short Form</h3>
                      <p className="text-sm text-muted-foreground mt-1 mb-3">
                        Make it easy to take action.
                      </p>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li>• Repeat your main CTA</li>
                        <li>• Short form: Name, phone, address, message</li>
                        <li>• Click-to-call button (especially mobile)</li>
                        <li>• Response time expectation: "We reply within 2 hours"</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Internal Linking Pattern */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">Internal Linking Pattern</h2>
              
              <p className="text-muted-foreground mb-6">
                Service pages should not be islands. Connect them to the rest of your site for better SEO and user experience.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-card rounded-lg border">
                  <LinkIcon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Service Page → Related Services</h3>
                    <p className="text-sm text-muted-foreground">
                      "Customers who get lawn aeration often add overseeding. Learn more about our overseeding service."
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-card rounded-lg border">
                  <LinkIcon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Service Page → Service Area Pages</h3>
                    <p className="text-sm text-muted-foreground">
                      "We provide lawn aeration in Springfield, Riverside, and Oak Park." Link each city to its{" "}
                      <NavLink to="/service-area-pages-local-seo/" className="text-primary hover:underline">service area page</NavLink>.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-card rounded-lg border">
                  <LinkIcon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Service Page → Homepage</h3>
                    <p className="text-sm text-muted-foreground">
                      Link your logo to homepage. Include "All Services" in your navigation.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-card rounded-lg border">
                  <LinkIcon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Homepage → Service Pages</h3>
                    <p className="text-sm text-muted-foreground">
                      Feature your top 3–5 services on the homepage with links to each service page.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* On-Page SEO Checklist */}
        <section className="py-12 md:py-16 bg-accent/30">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">On-Page SEO Checklist</h2>
              
              <p className="text-muted-foreground mb-6">
                Before publishing any service page, verify these elements:
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  "Title tag: Service + City + Brand (under 60 chars)",
                  "H1 matches search intent (one H1 only)",
                  "H2s break up content logically",
                  "URL is short and keyword-rich (/lawn-aeration/)",
                  "Meta description with service + location (under 160 chars)",
                  "Images have descriptive alt text",
                  "Page loads in under 3 seconds",
                  "Mobile-friendly layout (test it)",
                  "Internal links to related pages",
                  "Schema markup for LocalBusiness or Service"
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-2 p-3 bg-card rounded-lg border">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 p-4 bg-card rounded-lg border">
                <p className="text-sm text-muted-foreground">
                  <strong>Pro tip:</strong> Your{" "}
                  <NavLink to="/growth-add-ons/gbp" className="text-primary hover:underline">Google Business Profile</NavLink>{" "}
                  should list the same services. Consistency between your website and GBP helps local rankings.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Common Mistakes */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">Common Mistakes We Fix</h2>
              
              <div className="space-y-6">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <AlertTriangle className="h-6 w-6 text-orange-500 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold mb-2">All Services on One Page</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          <strong>Problem:</strong> One "Services" page listing 10 services in 3 sentences each.
                        </p>
                        <p className="text-sm text-muted-foreground">
                          <strong>Fix:</strong> Create dedicated pages for services with search volume. Keep the overview page as a hub linking to each.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <AlertTriangle className="h-6 w-6 text-orange-500 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold mb-2">Generic H1s ("Our Services")</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          <strong>Problem:</strong> Page titles and H1s that do not include the service or location.
                        </p>
                        <p className="text-sm text-muted-foreground">
                          <strong>Fix:</strong> Use specific, searchable H1s: "Lawn Mowing Services in Springfield, IL"
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <AlertTriangle className="h-6 w-6 text-orange-500 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold mb-2">No Photos of Actual Work</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          <strong>Problem:</strong> Stock photos or no images at all.
                        </p>
                        <p className="text-sm text-muted-foreground">
                          <strong>Fix:</strong> Add 3–6 photos of your real projects. Before/after shots are most effective.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <AlertTriangle className="h-6 w-6 text-orange-500 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold mb-2">Missing or Hidden Contact Info</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          <strong>Problem:</strong> Visitors have to hunt for a phone number or form.
                        </p>
                        <p className="text-sm text-muted-foreground">
                          <strong>Fix:</strong> Put a CTA in the hero, mid-page, and at the bottom. Add a sticky call button on mobile.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <AlertTriangle className="h-6 w-6 text-orange-500 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold mb-2">No Pricing Information</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          <strong>Problem:</strong> Visitors leave to find a competitor who shows prices.
                        </p>
                        <p className="text-sm text-muted-foreground">
                          <strong>Fix:</strong> Add "starting at" prices or ranges. "Lawn mowing starting at $40/visit."
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Example Structures */}
        <section className="py-12 md:py-16 bg-accent/30">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">Service Page Structures (Examples)</h2>
              
              <p className="text-muted-foreground mb-8">
                Here is how to adapt the structure for different services:
              </p>
              
              <div className="space-y-8">
                {/* Lawn Mowing Example */}
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                      <FileText className="h-5 w-5 text-primary" />
                      Lawn Mowing Page Structure
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div className="p-3 bg-accent/50 rounded">
                        <strong>H1:</strong> Professional Lawn Mowing in [City], [State]
                      </div>
                      <div className="p-3 bg-accent/30 rounded">
                        <strong>Hero:</strong> "Keep your lawn looking sharp—weekly or bi-weekly mowing from $40/visit"
                      </div>
                      <div className="p-3 bg-accent/30 rounded">
                        <strong>What is Included:</strong> Mowing, edging, blowing off walkways/driveways, trimming around obstacles
                      </div>
                      <div className="p-3 bg-accent/30 rounded">
                        <strong>Frequency Options:</strong> Weekly, bi-weekly, or one-time. Seasonal contracts available.
                      </div>
                      <div className="p-3 bg-accent/30 rounded">
                        <strong>Related Services:</strong> Link to fertilization, aeration, leaf cleanup
                      </div>
                      <div className="p-3 bg-accent/30 rounded">
                        <strong>FAQ:</strong> "How often should I mow?" "What height do you cut?" "Do you bag clippings?"
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Mulch Install Example */}
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                      <FileText className="h-5 w-5 text-primary" />
                      Mulch Installation Page Structure
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div className="p-3 bg-accent/50 rounded">
                        <strong>H1:</strong> Mulch Installation and Delivery in [City]
                      </div>
                      <div className="p-3 bg-accent/30 rounded">
                        <strong>Hero:</strong> "Fresh mulch beds that last all season—installed in one day"
                      </div>
                      <div className="p-3 bg-accent/30 rounded">
                        <strong>What is Included:</strong> Bed edging, weed removal, mulch delivery and spreading, cleanup
                      </div>
                      <div className="p-3 bg-accent/30 rounded">
                        <strong>Mulch Types:</strong> Hardwood, cedar, dyed (black/brown/red), rubber. Show photos of each.
                      </div>
                      <div className="p-3 bg-accent/30 rounded">
                        <strong>Pricing:</strong> "Starting at $75/yard installed" or price-per-square-foot
                      </div>
                      <div className="p-3 bg-accent/30 rounded">
                        <strong>FAQ:</strong> "How much mulch do I need?" "How deep should mulch be?" "When is the best time?"
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Hardscaping/Patio Example */}
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                      <FileText className="h-5 w-5 text-primary" />
                      Hardscaping / Patio Page Structure
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div className="p-3 bg-accent/50 rounded">
                        <strong>H1:</strong> Patio Installation and Hardscaping in [City]
                      </div>
                      <div className="p-3 bg-accent/30 rounded">
                        <strong>Hero:</strong> "Custom patios, walkways, and retaining walls—designed for your outdoor living space"
                      </div>
                      <div className="p-3 bg-accent/30 rounded">
                        <strong>Services Covered:</strong> Patios, walkways, retaining walls, fire pits, outdoor kitchens
                      </div>
                      <div className="p-3 bg-accent/30 rounded">
                        <strong>Materials:</strong> Pavers, natural stone, concrete, brick. Show examples of each.
                      </div>
                      <div className="p-3 bg-accent/30 rounded">
                        <strong>Process:</strong> Design consultation → 3D rendering → Material selection → Installation → Final walkthrough
                      </div>
                      <div className="p-3 bg-accent/30 rounded">
                        <strong>Gallery:</strong> Heavy on before/after photos. Show project variety and scale.
                      </div>
                      <div className="p-3 bg-accent/30 rounded">
                        <strong>FAQ:</strong> "How long does a patio take?" "What is the cost per square foot?" "Do you handle permits?"
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">Frequently Asked Questions</h2>
              
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-16 bg-primary/5">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Want Service Pages Built the Right Way?</h2>
              
              <p className="text-muted-foreground mb-6">
                We build optimized service pages into every{" "}
                <NavLink to="/websites" className="text-primary hover:underline">website subscription</NavLink>. 
                You tell us your services—we create pages that rank and convert.
              </p>
              
              <p className="text-muted-foreground mb-8">
                Have questions about your specific services? Let us know. Book a 15-minute call or{" "}
                <NavLink to="/contact" className="text-primary hover:underline">send us a message</NavLink>.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="#get-started">
                  <Button size="lg" className="w-full sm:w-auto">
                    Get Started
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
                <NavLink to="/websites">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto">
                    See Website Subscription Pricing
                  </Button>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* Related Reading */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-3xl">
              <h2 className="text-2xl font-bold mb-6">Related Reading</h2>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <NavLink to="/lawn-care-website-design/" className="block p-4 bg-card rounded-lg border hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Lawn Care Website Design</h3>
                  <p className="text-sm text-muted-foreground">Done-for-you websites for lawn care companies</p>
                </NavLink>
                
                <NavLink to="/landscaping-website-design/" className="block p-4 bg-card rounded-lg border hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Landscaping Website Design</h3>
                  <p className="text-sm text-muted-foreground">Websites for landscapers that convert visitors</p>
                </NavLink>
                
                <NavLink to="/service-area-pages-local-seo/" className="block p-4 bg-card rounded-lg border hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Service Area Pages Guide</h3>
                  <p className="text-sm text-muted-foreground">Rank in nearby cities with optimized city pages</p>
                </NavLink>
                
                <NavLink to="/resources" className="block p-4 bg-card rounded-lg border hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Free Resources</h3>
                  <p className="text-sm text-muted-foreground">Checklists, guides, and tips for your business</p>
                </NavLink>
              </div>
              
              <div className="mt-6 text-center">
                <NavLink to="/blog" className="text-primary hover:underline text-sm">
                  Read more on our blog →
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* Calendly Section */}
        <CalendlyEmbed />

        {/* Last Updated */}
        <section className="py-8">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-sm text-muted-foreground">
                Last updated: December 19, 2024
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ServicePagesThatRank;